import React, { useState } from 'react';
import { usePayment } from '../context/PaymentContext';
import { useAuth } from '../context/AuthContext';
import PaymentGateway from '../components/payment/PaymentGateway';
import LoadingSpinner from '../components/common/LoadingSpinner';
import './SubscriptionPlans.css';

const SubscriptionPlans: React.FC = () => {
  const { selectPlan, selectedPlan, loading, error, clearError } = usePayment();
  const { isAuthenticated, user } = useAuth();
  const [showPaymentGateway, setShowPaymentGateway] = useState(false);

  const handlePlanSelect = (plan: any) => {
    if (!isAuthenticated) {
      alert('Please login first to select a plan');
      return;
    }
    selectPlan(plan);
    setShowPaymentGateway(true);
  };

  const handlePaymentSuccess = () => {
    // Payment was successful, user will be redirected
    setShowPaymentGateway(false);
  };

  const handlePaymentClose = () => {
    setShowPaymentGateway(false);
    clearError();
  };

  const clearErrorHandler = () => {
    clearError();
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="subscription-container">
      <section className="subscription-header">
        <h1>Choose Your Perfect Plan</h1>
        <p>Unlock unlimited access to all premium astrological features with our flexible subscription plans</p>
      </section>

      {error && (
        <div className="error-banner">
          <span>{error}</span>
          <button onClick={clearErrorHandler} className="close-button">×</button>
        </div>
      )}

      <section className="plans-section">
        <div className="plans-grid">
          {/* Daily Pass */}
          <div className="plan-card">
            <div className="plan-header">
              <h3 className="plan-name">Daily Pass</h3>
              <div className="plan-price">
                <span className="plan-currency">₹</span>
                <span className="plan-amount">10</span>
              </div>
              <div className="plan-duration">per day</div>
            </div>
            <p className="plan-description">Unlimited access for 24 hours</p>
            <ul className="plan-features">
              <li className="plan-feature">All premium features</li>
              <li className="plan-feature">24-hour access</li>
              <li className="plan-feature">Cancel anytime</li>
            </ul>
            <button 
              className="plan-button secondary" 
              onClick={() => handlePlanSelect({
                type: 'daily_pass',
                name: 'Daily Pass',
                price: 10,
                duration: 'per day',
                description: 'Unlimited access for 24 hours',
                features: ['All premium features', '24-hour access'],
                isSubscription: true
              })}
            >
              Get Daily Pass
            </button>
          </div>

          {/* Weekly Pass */}
          <div className="plan-card popular">
            <div className="plan-header">
              <h3 className="plan-name">Weekly Pass</h3>
              <div className="plan-price">
                <span className="plan-currency">₹</span>
                <span className="plan-amount">49</span>
              </div>
              <div className="plan-duration">per week</div>
            </div>
            <p className="plan-description">Unlimited access for 7 days</p>
            <ul className="plan-features">
              <li className="plan-feature">All premium features</li>
              <li className="plan-feature">7-day access</li>
              <li className="plan-feature">Best value for short term</li>
            </ul>
            <button 
              className="plan-button primary" 
              onClick={() => handlePlanSelect({
                type: 'weekly_pass',
                name: 'Weekly Pass',
                price: 49,
                duration: 'per week',
                description: 'Unlimited access for 7 days',
                features: ['All premium features', '7-day access', 'Best value for short term'],
                isSubscription: true
              })}
            >
              Get Weekly Pass
            </button>
          </div>

          {/* Monthly Pass */}
          <div className="plan-card">
            <div className="plan-header">
              <h3 className="plan-name">Monthly Pass</h3>
              <div className="plan-price">
                <span className="plan-currency">₹</span>
                <span className="plan-amount">99</span>
              </div>
              <div className="plan-duration">per month</div>
            </div>
            <p className="plan-description">Unlimited access for 30 days</p>
            <ul className="plan-features">
              <li className="plan-feature">All premium features</li>
              <li className="plan-feature">30-day access</li>
              <li className="plan-feature">Save 60% vs daily</li>
            </ul>
            <button 
              className="plan-button secondary" 
              onClick={() => handlePlanSelect({
                type: 'monthly_pass',
                name: 'Monthly Pass',
                price: 99,
                duration: 'per month',
                description: 'Unlimited access for 30 days',
                features: ['All premium features', '30-day access', 'Save 60% vs daily'],
                isSubscription: true
              })}
            >
              Get Monthly Pass
            </button>
          </div>

          {/* Quarterly Pass */}
          <div className="plan-card">
            <div className="plan-header">
              <h3 className="plan-name">Quarterly Pass</h3>
              <div className="plan-price">
                <span className="plan-currency">₹</span>
                <span className="plan-amount">199</span>
              </div>
              <div className="plan-duration">per 3 months</div>
            </div>
            <p className="plan-description">Unlimited access for 90 days</p>
            <ul className="plan-features">
              <li className="plan-feature">All premium features</li>
              <li className="plan-feature">90-day access</li>
              <li className="plan-feature">Save 67% vs monthly</li>
            </ul>
            <button 
              className="plan-button secondary" 
              onClick={() => handlePlanSelect({
                type: 'quarterly_pass',
                name: 'Quarterly Pass',
                price: 199,
                duration: 'per 3 months',
                description: 'Unlimited access for 90 days',
                features: ['All premium features', '90-day access', 'Save 67% vs monthly'],
                isSubscription: true
              })}
            >
              Get Quarterly Pass
            </button>
          </div>

          {/* Yearly Pass */}
          <div className="plan-card popular">
            <div className="plan-header">
              <h3 className="plan-name">Yearly Pass</h3>
              <div className="plan-price">
                <span className="plan-currency">₹</span>
                <span className="plan-amount">999</span>
              </div>
              <div className="plan-duration">per year</div>
            </div>
            <p className="plan-description">Unlimited access for 365 days</p>
            <ul className="plan-features">
              <li className="plan-feature">All premium features</li>
              <li className="plan-feature">365-day access</li>
              <li className="plan-feature">Save 83% vs monthly</li>
              <li className="plan-feature">Priority support</li>
            </ul>
            <button 
              className="plan-button primary" 
              onClick={() => handlePlanSelect({
                type: 'yearly_pass',
                name: 'Yearly Pass',
                price: 999,
                duration: 'per year',
                description: 'Unlimited access for 365 days',
                features: ['All premium features', '365-day access', 'Save 83% vs monthly', 'Priority support'],
                isSubscription: true
              })}
            >
              Get Yearly Pass
            </button>
          </div>
        </div>
      </section>

      {showPaymentGateway && selectedPlan && (
        <PaymentGateway 
          plan={selectedPlan} 
          onClose={handlePaymentClose}
          onSuccess={handlePaymentSuccess}
        />
      )}
    </div>
  );
};

export default SubscriptionPlans;
